import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Badge,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import { Close, Send, Search, ArrowBack, Message } from "@mui/icons-material";
import Parse from "parse";
import { useNotify } from "react-admin";
import { COLORS, inputSx, filterInputSx, formatTime } from "./messagingUtils";

const MessagingWidget = ({ open, onClose, selectedUser = null }) => {
  const [view, setView] = useState("list");
  const [convs, setConvs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [filterRole, setFilterRole] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const endRef = useRef(null);
  const timerRef = useRef(null);
  const notify = useNotify();
  const user = Parse.User.current();
  const userRole = user?.get("role")?.get("name");

  const scrollEnd = () => endRef.current?.scrollIntoView({ behavior: "smooth" });

  const loadConvs = useCallback(async () => {
    try {
      setLoading(true);
      setConvs((await Parse.Cloud.run("getConversations")) || []);
    } catch (e) {
      notify("Failed to load conversations", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  const loadMsgs = useCallback(
    async (recipientId) => {
      if (!recipientId) return;
      try {
        setLoading(true);
        const res = await Parse.Cloud.run("getMessages", { recipientId });
        setMsgs((res || []).reverse());
      } catch (e) {
        notify("Failed to load messages", { type: "error" });
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  useEffect(() => {
    scrollEnd();
  }, [msgs]);

  useEffect(() => {
    if (open) {
      loadConvs();
      if (selectedUser)
        openChat({
          userId: selectedUser.userId || selectedUser.id,
          username: selectedUser.username,
          role: selectedUser.role,
        });
    }
  }, [open, selectedUser, loadConvs]);

  useEffect(() => {
    if (view !== "search" || !search.trim()) {
      setResults([]);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        setResults(
          (await Parse.Cloud.run("searchMessagableUsers", { searchTerm: search.trim() })) || []
        );
      } catch {
        notify("Search failed", { type: "error" });
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timerRef.current);
  }, [search, view, notify]);

  useEffect(() => {
    if (!open || !window.socket) return;
    
    if (user && window.socket.connected) {
      window.socket.emit("join_messaging", JSON.stringify({ userId: user.id }));
    }
    
    const handler = (data) => {
      const d = JSON.parse(data);
      notify(`New message from ${d.senderUsername || "User"}`, { type: "info" });
      loadConvs();
      if (current?.userId === d.senderId) loadMsgs(d.senderId);
    };
    
    window.socket.on("message_received", handler);
    return () => {
      window.socket.off("message_received", handler);
    };
  }, [open, current, user, loadConvs, loadMsgs, notify]);

  const openChat = async (u) => {
    const existing = convs.find((c) => c.otherUser.userId === u.userId);
    setCurrent(existing?.otherUser || u);
    setView("chat");
    await loadMsgs(u.userId);
  };

  const send = async () => {
    if (!text.trim() || !current) return;
    const temp = { id: "temp", senderId: user.id, message: text.trim(), createdAt: new Date() };
    setMsgs((p) => [...p, temp]);
    const msg = text.trim();
    setText("");
    setSending(true);
    try {
      const res = await Parse.Cloud.run("sendMessage", { recipientId: current.userId, message: msg });
      setMsgs((p) => p.map((m) => (m.id === "temp" ? { ...m, id: res.messageId } : m)));
      
      // Emit socket event for real-time delivery
      if (window.socket && window.socket.connected) {
        window.socket.emit(
          "new_message",
          JSON.stringify({ recipientId: current.userId, senderId: user.id, senderUsername: user.get("username"), message: msg })
        );
      }
      
      loadConvs();
    } catch (e) {
      notify(e.message || "Failed to send", { type: "error" });
      loadMsgs(current.userId);
    } finally {
      setSending(false);
    }
  };

  const back = () => {
    if (view === "chat") {
      setView("list");
      setCurrent(null);
      setMsgs([]);
      loadConvs();
    } else {
      setView("list");
      setSearch("");
      setResults([]);
    }
  };

  const RoleChip = ({ role }) => (
    <Chip
      label={role}
      size="small"
      sx={{ bgcolor: COLORS[role] || "#757575", color: "#fff", height: 20, fontSize: "0.7rem" }}
    />
  );

  const clearFilters = () => {
    setFilterRole("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const getFilteredConvs = () => {
    let filtered = [...convs];
    if (filterRole) {
      filtered = filtered.filter(c => c.otherUser.role === filterRole);
    }
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(c => new Date(c.lastMessageAt) >= fromDate);
    }
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(c => new Date(c.lastMessageAt) <= toDate);
    }
    return filtered;
  };

  const hasActiveFilters = filterRole || filterDateFrom || filterDateTo;
  const displayConvs = hasActiveFilters ? getFilteredConvs() : convs;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", sm: 400 }, bgcolor: "#242424", color: "#fff" } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", gap: 1 }}>
          {view !== "list" && (
            <IconButton onClick={back} size="small" sx={{ color: "#fff" }}>
              <ArrowBack />
            </IconButton>
          )}
          <Message sx={{ color: "#0288d1" }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {view === "chat" ? current?.username : view === "search" ? "Search" : "Messages"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>

        {view === "list" && (
          <>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => setView("search")}
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "rgba(255,255,255,0.5)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {userRole === "Super-User" && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: 1, border: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="subtitle2" sx={{ color: "#fff", mb: 1.5, fontWeight: 600 }}>
                    Filter Conversations
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Role"
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      sx={filterInputSx}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="" style={{ background: "#2a2a2a", color: "#fff" }}>All Roles</option>
                      <option value="Game-Owner" style={{ background: "#2a2a2a", color: "#fff" }}>Game-Owner</option>
                      <option value="Master-Distributor" style={{ background: "#2a2a2a", color: "#fff" }}>Master-Distributor</option>
                      <option value="Distributor" style={{ background: "#2a2a2a", color: "#fff" }}>Distributor</option>
                      <option value="Sub-Distributor" style={{ background: "#2a2a2a", color: "#fff" }}>Sub-Distributor</option>
                      <option value="Agent" style={{ background: "#2a2a2a", color: "#fff" }}>Agent</option>
                    </TextField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="From Date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        sx={filterInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="To Date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        sx={filterInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    {hasActiveFilters && (
                      <Button
                        size="small"
                        onClick={clearFilters}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.1)",
                          color: "white",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress sx={{ color: "#0288d1" }} />
                </Box>
              ) : !displayConvs.length ? (
                <Typography sx={{ p: 4, textAlign: "center", color: "rgba(255,255,255,0.7)" }}>
                  {hasActiveFilters ? "No conversations match filters" : "No conversations"}
                </Typography>
              ) : (
                <List>
                  {displayConvs.map((c) => (
                    <ListItemButton
                      key={c.otherUser.userId}
                      onClick={() => openChat(c.otherUser)}
                      sx={{ borderBottom: 1, borderColor: "rgba(255,255,255,0.12)" }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="subtitle2">{c.otherUser.username}</Typography>
                            <RoleChip role={c.otherUser.role} />
                            {c.unreadCount > 0 && <Badge badgeContent={c.unreadCount} color="error" />}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: "70%", color: "rgba(255,255,255,0.6)" }}>
                              {c.lastMessage}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                              {formatTime(c.lastMessageAt)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </>
        )}

        {view === "search" && (
          <>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={inputSx}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "rgba(255,255,255,0.5)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: loading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} sx={{ color: "#0288d1" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {!results.length ? (
                <Typography sx={{ p: 4, textAlign: "center", color: "rgba(255,255,255,0.7)" }}>
                  {search ? "No users found" : "Enter username"}
                </Typography>
              ) : (
                <List>
                  {results.map((u) => (
                    <ListItemButton key={u.userId} onClick={() => openChat(u)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="subtitle2">{u.username}</Typography>
                            <RoleChip role={u.role} />
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </>
        )}

        {view === "chat" && current && (
          <>
            <Box sx={{ p: 1, bgcolor: "rgba(0,0,0,0.2)", borderBottom: 1, borderColor: "rgba(255,255,255,0.12)" }}>
              <RoleChip role={current.role} />
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, bgcolor: "#1a1a1a" }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !msgs.length ? (
                <Typography sx={{ py: 4, textAlign: "center", color: "rgba(255,255,255,0.7)" }}>
                  No messages yet. Start the conversation!
                </Typography>
              ) : (
                msgs.map((m, i) => {
                  const own = m.senderId === user?.id;
                  return (
                    <Box key={m.id || i} sx={{ display: "flex", justifyContent: own ? "flex-end" : "flex-start", mb: 2 }}>
                      <Paper sx={{ p: 1.5, maxWidth: "75%", bgcolor: own ? "#0288d1" : "#2a2a2a", color: "#fff" }}>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {m.message}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7, fontSize: "0.65rem" }}>
                          {formatTime(m.createdAt)}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                })
              )}
              <div ref={endRef} />
            </Box>
            {current.role !== "Super-User" && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: "rgba(255,255,255,0.12)" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type a message..."
                  value={text}
                  onChange={(e) => e.target.value.length <= 500 && setText(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  multiline
                  maxRows={3}
                  disabled={sending}
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{ mr: 1, color: "rgba(255,255,255,0.7)" }}
                        >
                          {text.length}/500
                        </Typography>
                        <IconButton onClick={send} disabled={!text.trim() || sending} sx={{ color: "#0288d1" }}>
                          {sending ? <CircularProgress size={20} /> : <Send />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default MessagingWidget;
